import '../../assets/css/case-property.css';
// @ts-ignore
import { useProperty, usePage, useContentToken, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesRight } from '@fortawesome/free-solid-svg-icons';
import { getPropertyBasePath, getStateName } from './propertyUtils';
import caseBackgroundIcon from '../../assets/images/case-background.svg';
import { openTourScheduler } from './TourSchedulerModal';

interface PageMeta {
  breadcrumb: string;
  defaultTitle: (name: string, city: string, state: string, stateName: string) => string;
  defaultBlurb: (name: string, city: string) => string;
  defaultCtaText: string;
  /** When set, CTA URL defaults to this path relative to basePath; otherwise defaults to /contact-us */
  defaultCtaPath?: string;
  /** When true, CTA opens the tour scheduler modal instead of navigating */
  openTourModal?: boolean;
  /** Property field to pull blurb from if no content override exists */
  blurbField?: string;
  /** When set, renders the title as richtext with embedded field tokens instead of plain text */
  titleRichtext?: string;
}

const PAGE_META: Record<string, PageMeta> = {
  'property-floor-plans': {
    breadcrumb: 'Floor Plans',
    defaultTitle: (_n, city, _s, _sn) => `1-, 2-, and 3-Bedroom Apartments in ${city}`,
    defaultBlurb: (_n, city) => `We offer unique floor plans at our ${city} apartment community, each offering the perfect backdrop to your lifestyle.`,
    defaultCtaText: 'Contact Us',
    blurbField: 'property_home_floorplans_blurb',
    titleRichtext: '<span data-type="field-token" data-field="property_bedrooms_range"></span> Bedroom <span data-type="field-token" data-field="property_type"></span> in <span data-type="field-token" data-field="property_address_city"></span>',
  },
  'property-amenities': {
    breadcrumb: 'Amenities',
    defaultTitle: (name, _c, _s, _sn) => `Amenities at ${name}`,
    defaultBlurb: (name) => `Discover the amenities available at ${name}.`,
    defaultCtaText: 'Contact Us',
    blurbField: 'property_amenities_blurb',
  },
  'property-neighborhood': {
    breadcrumb: 'Neighborhood',
    defaultTitle: (name, city, state, _sn) => `${name} Neighborhood in ${city}, ${state}`,
    defaultBlurb: (name) => `Discover the vibrant neighborhood surrounding ${name}.`,
    defaultCtaText: 'Contact Us',
    blurbField: 'property_home_neighborhood_blurb',
  },
  'property-gallery': {
    breadcrumb: 'Gallery',
    defaultTitle: (name, _c, _s, _sn) => `Gallery Images at ${name}`,
    defaultBlurb: (name) => `Browse our photo gallery at ${name}.`,
    defaultCtaText: 'Contact Us',
  },
  'property-pet-policy': {
    breadcrumb: 'Pet Policy',
    defaultTitle: (name, city, _s, stateName) => `${name}: Pet-Friendly Homes in ${city}, ${stateName}`,
    defaultBlurb: (name, city) => `We invite you to bring your pets to live at ${name}, where you'll find comfortable, stylish, pet-friendly apartments in ${city}. Learn more about our pet policy below.`,
    defaultCtaText: 'Contact Us',
    defaultCtaPath: '/contact-us',
    blurbField: 'property_pets_blurb',
  },
  'property-contact': {
    breadcrumb: 'Contact Us',
    defaultTitle: (name, _c, _s, _sn) => `Contact ${name}`,
    defaultBlurb: (name) => `Get in touch with ${name}. We'd love to hear from you.`,
    defaultCtaText: 'Schedule a Tour',
    openTourModal: true,
  },
};

function detectPageType(alias: string): string {
  for (const prefix of Object.keys(PAGE_META)) {
    if (alias.startsWith(prefix)) return prefix;
  }
  return '';
}

export function PropertySubpageHeader() {
  const property = useProperty();
  const page = usePage();
  const alias = page?.alias || '';

  const name = property.property_name || '';
  const city = property.property_address_city || '';
  const stateAbbr = property.property_address_state || '';
  const stateName = getStateName(stateAbbr);
  const basePath = getPropertyBasePath(property);
  const stateSlug = property._stateSlug || stateAbbr.toLowerCase();

  const pageType = detectPageType(alias);
  const meta = PAGE_META[pageType];

  if (!meta) return null;

  const fallbackTitle = meta.defaultTitle(name, city, stateAbbr, stateName);

  const title = useContentToken('propertysubpageheader.title') || fallbackTitle;
  const ctaText = useContentToken('propertysubpageheader.cta_text') || meta.defaultCtaText;
  const defaultCtaUrl = meta.defaultCtaPath ? `${basePath}${meta.defaultCtaPath}` : `${basePath}/contact-us`;
  const ctaUrlToken = useContentToken('propertysubpageheader.cta_url');
  const ctaUrl = ctaUrlToken || defaultCtaUrl;
  const shouldOpenTourModal = meta.openTourModal && !ctaUrlToken;

  return (
    <section className="prop-al-intro">
      <div
        className="prop-al-intro-overlay"
        role="presentation"
        aria-hidden="true"
      >
        <img src={caseBackgroundIcon} alt="Case & Associates logo accent background" />
      </div>
      <div className="prop-al-container-main">
        <div className="prop-al-intro-grid">
          <div className="prop-al-intro-left">
            <nav className="prop-al-breadcrumbs" aria-label="Breadcrumb">
              <ol>
                <li><a href="/">Home</a></li>
                <li><a href={`/apartments/${stateSlug}`}>{stateName}</a></li>
                <li><a href={basePath}>{name}</a></li>
                <li className="active" aria-current="page">{meta.breadcrumb}</li>
              </ol>
            </nav>
            {meta.titleRichtext ? (
              <RichTextContent
                path="propertysubpageheader.title"
                label="Page Title"
                defaultValue={meta.titleRichtext}
                as="h1"
                className="prop-al-title"
              />
            ) : (
              <EditableToken path="propertysubpageheader.title" type="text" label="Page Title" as="h1" className="prop-al-title" defaultValue={contentSchema.tokens['propertysubpageheader.title'].defaultValue}>
                {title || fallbackTitle}
              </EditableToken>
            )}
            <div className="prop-al-title-accent" />
          </div>

          <div className="prop-al-intro-right">
            <RichTextContent
              path="propertysubpageheader.blurb"
              label="Intro Text"
              defaultField={meta.blurbField ?? contentSchema.tokens['propertysubpageheader.blurb'].defaultField}
              defaultValue={contentSchema.tokens['propertysubpageheader.blurb'].defaultValue || meta.defaultBlurb(name, city)}
              as="div"
              className="prop-al-intro-text"
            />
            <EditableToken path="propertysubpageheader.cta_url" type="url" label="CTA Link URL" as="div" style={{ display: 'contents' }} defaultValue={contentSchema.tokens['propertysubpageheader.cta_url'].defaultValue}>
              <EditableToken path="propertysubpageheader.cta_text" type="text" label="CTA Text" as="span" style={{ display: 'contents' }} defaultValue={contentSchema.tokens['propertysubpageheader.cta_text'].defaultValue}>
                {shouldOpenTourModal ? (
                  <button type="button" className="prop-al-contact-link" onClick={openTourScheduler} data-vibesite-event="schedule_tour_clicked" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit' }}>
                    {ctaText}
                    <FontAwesomeIcon icon={faAnglesRight} className="prop-al-link-arrow" />
                  </button>
                ) : (
                  <a className="prop-al-contact-link" href={ctaUrl}>
                    {ctaText}
                    <FontAwesomeIcon icon={faAnglesRight} className="prop-al-link-arrow" />
                  </a>
                )}
              </EditableToken>
            </EditableToken>
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertySubpageHeader',
  displayName: 'Property Subpage Header',
  tokens: {
    'propertysubpageheader.title': {
      label: 'Page Title',
      type: 'richtext',
      defaultValue: '',
    },
    'propertysubpageheader.blurb': {
      label: 'Intro Text',
      type: 'richtext',
      defaultValue: '',
    },
    'propertysubpageheader.cta_text': {
      label: 'CTA Link Text',
      type: 'text',
      defaultValue: '',
    },
    'propertysubpageheader.cta_url': {
      label: 'CTA Link URL',
      type: 'url',
      defaultValue: '',
    },
  },
};

(PropertySubpageHeader as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertySubpageHeader;
